import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { FirstVisitHint, HelpButton } from '../help/HelpPanel';
import { api } from '../lib/api';
import { canAny, useStaff, type Role, type StaffMe } from '../lib/auth';
import { roleLabel } from '../lib/format';
import { ThemeToggle } from '../pages/staff/engagement/ThemeToggle';
import { LiveDot } from '../pages/staff/live/LiveDot';
import { useOutletList } from '../pages/staff/admin/common';
import { Select, Spinner } from './ui';

/**
 * Venue modules whose end-to-end spec is not green yet: their pages work by address but stay out of the menu.
 * A module leaves this list once its server tests and its end-to-end spec pass. Every module is live now
 * (parking, activities, settlements, facility, people, supply, cards and feedback).
 */
export const NOT_YET_LIVE = new Set<string>([]);

export interface NavItem {
  to: string;
  label: string;
  /** One permission, or a list of which any one is enough. */
  perm: string | readonly string[];
  section: string;
  /** Only the super admin and the accountant see it (venue-wide pages). */
  venueOnly?: boolean;
}

/** The venue menu: everyone except shop logins sees the entries their permissions allow, in this order. */
export const NAV: NavItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', perm: 'reports.read', section: 'Overview' },
  { to: '/admin/shops', label: 'All shops', perm: 'shops.overview', section: 'Overview' },
  { to: '/admin/shops/daily', label: 'Daily sales by shop', perm: 'shops.overview', section: 'Overview' },
  // Every login may open the feed: the server sends each person only what they may see.
  { to: '/admin/live-activity', label: 'Live activity', perm: 'live.view', section: 'Overview' },
  { to: '/admin/gate', label: 'Gate & vehicles inside', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/visitors', label: 'Visitors & PAX', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/live', label: 'Live park status', perm: 'reports.read', section: 'Parking' },
  { to: '/admin/collect', label: 'Collect payment', perm: 'payments.record', section: 'Parking' },
  { to: '/admin/visits', label: 'Visit history', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/printer', label: 'Printer', perm: 'visits.write', section: 'Parking' },
  { to: '/admin/outlets', label: 'Shops & outlets', perm: 'outlets.manage', section: 'Shops' },
  { to: '/admin/games', label: 'Games & rides', perm: 'bookings.operate', section: 'Activities' },
  { to: '/admin/turf', label: 'Turf booking', perm: 'bookings.operate', section: 'Activities' },
  { to: '/admin/events', label: 'Events', perm: 'bookings.operate', section: 'Activities' },
  { to: '/admin/settlements', label: 'Shop settlements', perm: 'settlements.read', section: 'Commerce' },
  { to: '/admin/agreements', label: 'Rentals & holding', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/closures', label: 'Closure register', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/advertisements', label: 'Advertisements', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/cleaning-charges', label: 'Cleaning charges', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/generator', label: 'Generator & allocation', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/housekeeping', label: 'Housekeeping', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/quality', label: 'Quality control', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/pest-control', label: 'Pest control', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/maintenance', label: 'Maintenance', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/assets', label: 'Assets', perm: 'facility.manage', section: 'Facility' },
  { to: '/admin/other-services', label: 'Other services', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/employees', label: 'Employees', perm: ['people.operate', 'people.manage'], section: 'People' },
  { to: '/admin/attendance', label: 'Attendance', perm: 'people.operate', section: 'People' },
  { to: '/admin/payroll', label: 'Payroll', perm: 'payroll.manage', section: 'People' },
  { to: '/admin/hr-reminders', label: 'HR payment reminders', perm: ['people.manage', 'payroll.manage'], section: 'People' },
  { to: '/admin/accommodation', label: 'Accommodation', perm: 'people.operate', section: 'People' },
  { to: '/admin/materials', label: 'Materials & stock', perm: ['supply.operate', 'supply.manage'], section: 'Supply chain' },
  { to: '/admin/material-requests', label: 'Material requests', perm: ['supply.operate', 'supply.manage'], section: 'Supply chain' },
  { to: '/admin/procurement', label: 'Procurement', perm: 'supply.manage', section: 'Supply chain' },
  { to: '/admin/supplier-bills', label: 'Supplier bills', perm: 'supply.manage', section: 'Supply chain' },
  { to: '/admin/payments', label: 'Payments', perm: 'payments.read', section: 'Money' },
  { to: '/admin/closing', label: 'Daily closing', perm: 'closings.read', section: 'Money' },
  { to: '/admin/finance', label: 'Income & expenses', perm: 'finance.read', section: 'Money' },
  { to: '/admin/reports', label: 'Reports', perm: 'reports.read', section: 'Money' },
  { to: '/admin/customers', label: 'Customers', perm: 'customers.lookup', section: 'Customers' },
  { to: '/admin/cards', label: 'Cards & recharge', perm: 'cards.operate', section: 'Customers' },
  { to: '/admin/coupons', label: 'Redeem coupon', perm: 'coupons.redeem', section: 'Customers' },
  { to: '/admin/campaigns', label: 'Campaigns', perm: 'campaigns.read', section: 'Customers' },
  { to: '/admin/notifications', label: 'Notifications', perm: 'notifications.manage', section: 'Customers' },
  { to: '/admin/feedback', label: 'Feedback', perm: 'feedback.read', section: 'Customers' },
  { to: '/admin/users', label: 'Staff users', perm: 'users.manage', section: 'Admin' },
  { to: '/admin/settings', label: 'Settings', perm: 'settings.manage', section: 'Admin' },
  { to: '/admin/pricing', label: 'Pricing rules', perm: 'pricing.read', section: 'Admin' },
  { to: '/admin/cameras', label: 'Cameras & plates', perm: 'cameras.manage', section: 'Admin' },
  { to: '/admin/areas', label: 'Vehicle areas (RTO)', perm: 'settings.manage', section: 'Admin' },
  { to: '/admin/imports', label: 'Data import', perm: 'imports.manage', section: 'Admin' },
  { to: '/admin/terms', label: 'Terms & conditions', perm: 'terms.manage', section: 'Admin' },
  { to: '/admin/audit', label: 'Audit log', perm: 'audit.read', section: 'Admin' },
];

/** A shop's own menu, for its admin and counter logins (one "My shop" section). */
export const SHOP_NAV: NavItem[] = [
  { to: '/admin/shop-home', label: 'Home', perm: 'shop.home', section: 'My shop' },
  { to: '/admin/pos', label: 'Billing', perm: 'pos.sell', section: 'My shop' },
  { to: '/admin/live-activity', label: 'Live activity', perm: 'live.view', section: 'My shop' },
  { to: '/admin/bills', label: 'Bills', perm: 'sales.read', section: 'My shop' },
  { to: '/admin/products', label: 'Products & stock', perm: 'products.read', section: 'My shop' },
  { to: '/admin/finance', label: 'Expenses', perm: 'finance.read', section: 'My shop' },
  { to: '/admin/closing', label: 'Close the day', perm: 'closings.read', section: 'My shop' },
  { to: '/admin/shop-pnl', label: 'Profit & loss', perm: 'shop.reports', section: 'My shop' },
  { to: '/admin/shop-staff', label: 'Staff logins', perm: 'shop.users', section: 'My shop' },
  { to: '/admin/shop-settings', label: 'Receipt & shop details', perm: 'shop.settings', section: 'My shop' },
  { to: '/admin/printer', label: 'Printer', perm: 'pos.sell', section: 'My shop' },
  { to: '/admin/settlements', label: 'Venue statements', perm: 'settlements.read', section: 'My shop' },
];

/** What the super admin and the accountant see inside one shop ("All shops" → Open shop). */
const DRILL_NAV: NavItem[] = [
  { to: '/admin/shop-home', label: 'Home', perm: 'shop.home', section: 'Shop' },
  { to: '/admin/bills', label: 'Bills', perm: 'sales.read', section: 'Shop' },
  { to: '/admin/products', label: 'Products & stock', perm: 'products.read', section: 'Shop' },
  { to: '/admin/shop-pnl', label: 'Profit & loss', perm: 'shop.reports', section: 'Shop' },
  { to: '/admin/shop-staff', label: 'Staff logins', perm: 'shop.users', section: 'Shop' },
  { to: '/admin/shop-settings', label: 'Receipt & shop details', perm: 'shop.settings', section: 'Shop' },
  { to: '/admin/closing', label: 'Daily closing', perm: 'closings.read', section: 'Shop' },
  { to: '/admin/finance', label: 'Expenses', perm: 'finance.read', section: 'Shop' },
];
/** Added to the shop's menu while the super admin works the counter of a venue-run outlet. */
const COUNTER_NAV: NavItem[] = [
  { to: '/admin/pos', label: 'Billing', perm: 'pos.sell', section: 'Shop' },
  { to: '/admin/printer', label: 'Printer', perm: 'pos.sell', section: 'Shop' },
];

/** Pages that belong to one shop: opening one as the super admin opens that shop's menu. */
const SHOP_ROUTES = ['/admin/shop-home', '/admin/pos', '/admin/bills', '/admin/products', '/admin/shop-pnl', '/admin/shop-staff', '/admin/shop-settings'];
/** Pages that work for a shop or for the venue: they keep whichever menu is open. */
const SHARED_ROUTES = ['/admin/closing', '/admin/finance', '/admin/printer', '/admin/account'];
/** Venue pages that work on one vehicle gate (the gate picker in the header chooses it). */
const GATE_ROUTES = ['/admin/gate', '/admin/collect', '/admin/visitors', '/admin/live', '/admin/closing', '/admin/printer'];
/** The accountant's menu: overview, money and the money-related venue pages (statements, payroll). */
const ACCOUNTANT_SECTIONS = new Set(['Overview', 'Money', 'Commerce', 'People']);

const SHOP_ROLES: Role[] = ['shop_admin', 'shop_staff'];
const under = (path: string, routes: string[]) => routes.some((r) => path === r || path.startsWith(`${r}/`));

export interface Menu {
  sections: { title: string; items: NavItem[] }[];
}

function group(items: NavItem[]): Menu {
  const titles = [...new Set(items.map((i) => i.section))];
  return { sections: titles.map((title) => ({ title, items: items.filter((i) => i.section === title) })) };
}

/** The menu a login sees right now. Each entry is also guarded by its route, so this only decides what is offered. */
export function menuFor(
  me: StaffMe,
  can: (perm: string) => boolean,
  opts: { viewingShop?: boolean; workAtCounter?: boolean; simulation?: boolean; venueRunShop?: boolean } = {},
): Menu {
  const role = me.user.role;
  const allowed = (i: NavItem) => canAny(can, i.perm) && !NOT_YET_LIVE.has(i.to);
  // A shop run by the venue itself pays no rent or commission, so it never gets venue statements: no empty page.
  if (SHOP_ROLES.includes(role)) return group(SHOP_NAV.filter((i) => allowed(i) && !(opts.venueRunShop && i.to === '/admin/settlements')));
  if (opts.viewingShop) return group([...DRILL_NAV, ...(opts.workAtCounter ? COUNTER_NAV : [])].filter(allowed));
  const venueWide = role === 'owner' || role === 'accountant';
  let items = NAV.filter((i) => allowed(i) && (!i.venueOnly || venueWide));
  if (role === 'accountant') items = items.filter((i) => ACCOUNTANT_SECTIONS.has(i.section));
  if (opts.simulation && can('simulation.run')) {
    const at = items.findIndex((i) => i.section !== 'Overview');
    items.splice(at < 0 ? items.length : at, 0, { to: '/admin/simulate', label: 'Simulate operations', perm: 'simulation.run', section: 'Overview' });
  }
  return group(items);
}

/**
 * The first screen after signing in: the most useful page for the role, else the first menu entry the login
 * may open, else My account. Never a page the login cannot open (that would bounce back here for ever).
 */
export function firstPage(me: StaffMe, can: (perm: string) => boolean): string {
  const role = me.user.role;
  const menu = menuFor(me, can).sections.flatMap((s) => s.items);
  const offered = (to: string) => menu.some((i) => i.to === to);
  const preferred: Partial<Record<Role, string[]>> = {
    security: ['/admin/gate'],
    cashier: ['/admin/collect'],
    shop_admin: ['/admin/shop-home', '/admin/shop-pnl'],
    shop_staff: ['/admin/pos', '/admin/shop-home'],
    owner: ['/admin/dashboard'],
    accountant: ['/admin/dashboard'],
  };
  // A venue login whose only module is not in the menu yet still lands on that module's page.
  const hidden = SHOP_ROLES.includes(role) ? undefined : NAV.find((i) => canAny(can, i.perm) && (!i.venueOnly || role === 'owner' || role === 'accountant'));
  // Every login has the live feed; it is a page to look in on, not where the day's work starts.
  const work = menu.find((i) => i.to !== '/admin/live-activity');
  return (preferred[role] ?? []).find(offered) ?? work?.to ?? hidden?.to ?? menu[0]?.to ?? '/admin/account';
}

/** The shop's logo for its own logins' menu (admins read it from the shop profile, counters from the billing set-up). */
function useShopLogo(outletId: string | null, enabled: boolean) {
  const { can } = useStaff();
  const viaProfile = can('shop.settings');
  return useQuery({
    queryKey: ['shell', 'shop-logo', outletId, viaProfile],
    queryFn: async () => {
      if (viaProfile) return (await api<{ logo?: { url: string | null } | null }>(`/shops/${outletId}/profile`)).logo?.url ?? null;
      return (await api<{ logoUrl?: string | null }>('/sales/config', { query: { outletId } })).logoUrl ?? null;
    },
    enabled: enabled && !!outletId && (viaProfile || can('pos.sell')),
    // Logo links are signed for a few minutes.
    staleTime: 4 * 60_000,
    retry: false,
  });
}

/** Whether the simulation panel is switched on for this server (404 when it is not). */
function useSimulationEnabled(enabled: boolean) {
  const q = useQuery({
    queryKey: ['simulation', 'status'],
    queryFn: () => api<{ enabled?: boolean }>('/simulation/status'),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
  return !!q.data && q.data.enabled !== false;
}

/**
 * Whether a shop login's own shop is run by the venue itself (it then has no venue statements). The answer
 * comes from the same request the statements page makes, so opening that page afterwards costs nothing.
 */
function useOwnShopVenueRun(outletId: string | null, enabled: boolean) {
  const q = useQuery({
    queryKey: ['settlements', 'overview', outletId],
    queryFn: () => api<{ shop?: { operator_type?: string } }>('/settlements/overview', { query: { outletId } }),
    enabled: enabled && !!outletId,
    retry: false,
    staleTime: 10 * 60_000,
  });
  return q.data?.shop?.operator_type === 'venue_operated';
}

/**
 * The menu this login sees right now (also listed, with one line each, on "What I can do"), and whether the
 * shop the super admin has open is run by the venue (it may then work at its counter).
 */
export function useStaffMenu(): Menu & { venueRun: boolean } {
  const { me, can, outlet, outletId, venueWide, viewingShop, workAtCounter, mustChangePassword } = useStaff();
  const shopLogin = SHOP_ROLES.includes(me.user.role);
  const simulation = useSimulationEnabled(can('simulation.run'));
  const outlets = useOutletList(venueWide && viewingShop);
  const venueRun = !!viewingShop && (outlets.data as { id: string; operator_type?: string }[] | undefined)?.find((o) => o.id === outletId)?.operator_type === 'venue_operated';
  const venueRunShop = useOwnShopVenueRun(outletId, shopLogin && can('settlements.read') && outlet?.kind === 'shop' && !mustChangePassword);
  return useMemo(
    () => ({
      ...(mustChangePassword ? { sections: [] } : menuFor(me, can, { viewingShop, workAtCounter: workAtCounter && venueRun, simulation, venueRunShop })),
      venueRun,
    }),
    [me, can, viewingShop, workAtCounter, venueRun, simulation, venueRunShop, mustChangePassword],
  );
}

export function StaffLayout() {
  const { me, can, outlet, outletId, setOutletId, logout, venueWide, viewingShop, openShop, exitShop, workAtCounter, setWorkAtCounter, mustChangePassword } = useStaff();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const role = me.user.role;
  const shopLogin = SHOP_ROLES.includes(role);
  const menu = useStaffMenu();
  const venueRun = menu.venueRun;
  // Not before a handed-over password is changed: the API refuses everything but My account until then.
  const logo = useShopLogo(outletId, shopLogin && !mustChangePassword);
  const [logoBroken, setLogoBroken] = useState<string | null>(null);
  const gates = useMemo(() => me.outlets.filter((o) => o.kind === 'parking'), [me.outlets]);

  /*
   * The drawer and the account menu close when another page opens (a tap on a link, the back button, a
   * redirect). The router swaps pages inside a transition: the address bar changes at once, but the shown
   * page (and `path`) only once the next page's code has loaded, up to a second or two later on a phone. A
   * menu opened in between used to close itself when that late page change landed. Each menu remembers the
   * address it was opened on, and only a page other than that one closes it.
   */
  const openedAt = useRef<{ drawer: string | null; account: string | null }>({ drawer: null, account: null });
  const openDrawer = () => {
    openedAt.current.drawer = window.location.pathname;
    setOpen(true);
  };
  const toggleAccount = () => {
    openedAt.current.account = window.location.pathname;
    setMenuOpen((m) => !m);
  };
  useEffect(() => {
    if (openedAt.current.drawer !== path) setOpen(false);
    if (openedAt.current.account !== path) setMenuOpen(false);
  }, [path]);
  // While the drawer is open the page behind it must not scroll, and Escape closes it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Venue-wide logins: a shop's page opens that shop's menu; a venue page closes it; gate pages work on a gate.
  useEffect(() => {
    if (!venueWide) return;
    if (under(path, SHOP_ROUTES)) {
      if (!viewingShop && outlet?.kind === 'shop') openShop(outlet.id);
      return;
    }
    if (viewingShop && !under(path, SHARED_ROUTES)) exitShop();
    else if (!viewingShop && under(path, GATE_ROUTES) && outlet?.kind !== 'parking' && gates[0]) setOutletId(gates[0].id);
  }, [path, venueWide, viewingShop, outlet, gates, openShop, exitShop, setOutletId]);

  // The browser tab shows the shop's name for its own logins, the venue's name for everyone else.
  const brand = shopLogin && outlet ? outlet.name : me.business.name;
  useEffect(() => {
    document.title = brand;
  }, [brand]);

  if (mustChangePassword && path !== '/admin/account') return <Navigate to="/admin/account" replace />;

  const signOut = async () => {
    setMenuOpen(false);
    setOpen(false);
    await logout();
    navigate('/admin/login');
  };

  const logoUrl = shopLogin && logo.data && logoBroken !== logo.data ? logo.data : null;
  const brandBlock = (
    <span className="flex min-w-0 items-center gap-2.5">
      {logoUrl && <img src={logoUrl} alt="" className="h-8 w-8 shrink-0 rounded-md bg-white object-contain" onError={() => setLogoBroken(logo.data ?? null)} />}
      <span className="truncate" data-testid="shell-brand">
        {brand}
      </span>
    </span>
  );

  const nav = (
    <nav aria-label="Main" className="flex flex-col gap-4 px-3 py-4">
      {viewingShop && (
        <button
          type="button"
          onClick={() => {
            exitShop();
            navigate('/admin/shops');
          }}
          className="flex min-h-11 items-center rounded-md px-3 text-left text-sm text-slate-300 hover:bg-white/10 hover:text-white lg:min-h-9"
        >
          ← All shops
        </button>
      )}
      {menu.sections.map((s) => (
        <div key={s.title}>
          <p className="truncate px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{viewingShop ? (outlet?.name ?? s.title) : s.title}</p>
          {s.items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === '/admin/shops'}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                clsx('block rounded-md px-3 py-3 text-sm lg:py-1.5', isActive ? 'bg-brand-500 font-medium text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white')
              }
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      ))}
      {mustChangePassword && (
        <NavLink to="/admin/account" className="block rounded-md bg-brand-500 px-3 py-3 text-sm font-medium text-white lg:py-1.5">
          My account
        </NavLink>
      )}
      <div className="border-t border-white/10 pt-3">
        {/* A button like "← All shops" above: the menu's links are its pages; these two are about the login. */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate('/admin/guide');
          }}
          aria-current={path === '/admin/guide' ? 'page' : undefined}
          className={clsx(
            'flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm lg:min-h-9',
            path === '/admin/guide' ? 'bg-brand-500 font-medium text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white',
          )}
        >
          What I can do
        </button>
        <button type="button" onClick={signOut} className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm text-slate-300 hover:bg-white/10 hover:text-white lg:min-h-9">
          Sign out
        </button>
      </div>
    </nav>
  );

  // Gate staff and shop logins see (and switch) their outlets; venue-wide logins have no switcher (they open a
  // shop from "All shops" and pick a gate only on gate pages, when there is more than one gate).
  const picker = venueWide
    ? !viewingShop && under(path, GATE_ROUTES) && gates.length > 1
      ? { label: 'Gate', options: gates }
      : null
    : me.outlets.length > 0
      ? { label: 'Outlet', options: me.outlets }
      : null;

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden w-60 shrink-0 bg-shell lg:block">
        <div className="sticky top-0 flex max-h-screen flex-col overflow-y-auto">
          <div className="px-6 pb-2 pt-5 text-lg font-bold tracking-tight text-white">{brandBlock}</div>
          {nav}
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-slate-900/50" aria-label="Close menu" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col bg-shell shadow-2xl" aria-label="Menu">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 pl-6 pr-2 pt-[env(safe-area-inset-top)]">
              <span className="min-w-0 text-lg font-bold tracking-tight text-white">{brandBlock}</span>
              <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-slate-300 hover:bg-white/10" aria-label="Close menu" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">{nav}</div>
          </aside>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex h-14 items-center gap-2 pl-2 pr-3 sm:gap-3 sm:px-4">
            <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-slate-600 hover:bg-slate-100 lg:hidden" aria-label="Open menu" onClick={openDrawer}>
              ☰
            </button>
            <span className="hidden min-w-0 truncate font-semibold sm:block lg:hidden">{brand}</span>
            <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-1 sm:flex-none sm:gap-2">
              {picker && (
                <Select
                  aria-label={picker.label}
                  value={outletId ?? ''}
                  onChange={(e) => setOutletId(e.target.value)}
                  className="h-11 min-w-0 flex-1 sm:h-9 sm:w-52 sm:flex-none"
                >
                  {picker.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              )}
              {can('live.view') && !mustChangePassword && <LiveDot />}
              <ThemeToggle className="h-11 w-11 sm:h-9 sm:w-9" />
              <HelpButton path={path} role={role} />
              <div className="relative shrink-0">
                <button
                  onClick={toggleAccount}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="flex h-11 items-center gap-2 rounded-lg px-1.5 text-left hover:bg-slate-100"
                >
                  <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
                    {me.user.name.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hidden sm:block">
                    <span className="block text-sm font-medium leading-tight">{me.user.name}</span>
                    <span className="block text-xs text-slate-500">{roleLabel(role)}</span>
                  </span>
                  <span className="sr-only">Account menu</span>
                </button>
                {menuOpen && (
                  <>
                    <button className="fixed inset-0 z-40 cursor-default" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
                    {/* The top bar's blur keeps the backdrop above inside the bar; this one covers the page below it. */}
                    {createPortal(<button type="button" tabIndex={-1} aria-hidden className="fixed inset-0 z-20 cursor-default" onClick={() => setMenuOpen(false)} />, document.body)}
                    <div role="menu" className="absolute right-0 z-50 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500 sm:hidden">
                        {me.user.name} · {roleLabel(role)}
                      </p>
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/admin/account');
                        }}
                        className="block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        My account
                      </button>
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/admin/guide');
                        }}
                        className="block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        What I can do
                      </button>
                      <button role="menuitem" onClick={signOut} className="block min-h-11 w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-slate-50">
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {viewingShop && outlet && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-brand-100 bg-brand-50 px-4 py-1.5 text-sm text-brand-900" role="region" aria-label="Shop you are viewing">
              <p className="min-w-0 flex-1 basis-full truncate sm:basis-auto">
                Viewing <strong className="font-semibold">{outlet.name}</strong>
              </p>
              {venueRun && can('pos.sell') && (
                <label className="flex min-h-11 cursor-pointer items-center gap-2 lg:min-h-9">
                  <input type="checkbox" className="h-4 w-4 accent-brand-700" checked={workAtCounter} onChange={(e) => setWorkAtCounter(e.target.checked)} />
                  Work at this counter
                </label>
              )}
              <button
                type="button"
                onClick={() => {
                  exitShop();
                  navigate('/admin/shops');
                }}
                className="min-h-11 rounded-md px-3 font-medium text-brand-800 ring-1 ring-brand-200 hover:bg-white lg:min-h-8"
              >
                Exit
              </button>
            </div>
          )}
        </header>
        <main className="mx-auto max-w-7xl px-4 py-5 sm:py-6">
          {me.outlets.length === 0 && !['owner', 'accountant', 'venue_staff'].includes(role) && path !== '/admin/account' ? (
            <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900">You are not assigned to an outlet or shop yet. Ask the super admin (or your shop admin) to assign you.</p>
          ) : (
            // Pages load on demand: only the page area waits, so the menu and header stay put (and an open
            // phone menu stays open) while the next page's code arrives.
            <Suspense fallback={<Spinner />}>
              {!mustChangePassword && <FirstVisitHint path={path} role={role} userId={me.user.id} />}
              <Outlet />
            </Suspense>
          )}
        </main>
      </div>
    </div>
  );
}
